// Ops view — recent_runs + score_movements. Admin observability for the
// weekly orchestrator. Two tables; nothing fancy. Anyone can read; reads
// have a public_read RLS policy.

import type { CronRun, ScoreMovement } from "../lib/types.ts";

const escHtml = (s: string | null | undefined) =>
  String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );

const STATUS_DOT = {
  ok: "🟢",
  partial: "🟡",
  error: "🔴",
  running: "🔵",
  queued: "⚪",
};

export function renderOps(
  host: HTMLElement,
  runs: CronRun[],
  movements: ScoreMovement[],
): () => void {
  host.innerHTML = "";

  const runsTable = runs.length
    ? `<table class="ops-table">
        <thead><tr>
          <th>Status</th><th>Started</th><th>Duration</th>
          <th>Pairs</th><th>Changed</th><th>Trigger</th><th></th>
        </tr></thead>
        <tbody>${runs.map((r) => `
          <tr>
            <td>${STATUS_DOT[r.status as keyof typeof STATUS_DOT] ?? ""} ${r.status}</td>
            <td>${escHtml(r.started_at?.replace("T", " ").slice(0, 16) ?? "")}</td>
            <td>${r.duration_s != null ? `${r.duration_s}s` : "—"}</td>
            <td>${r.pairs_ok}/${r.pairs_total} ok${r.pairs_failed ? ` · ${r.pairs_failed} fail` : ""}</td>
            <td>${r.scores_changed}</td>
            <td>${escHtml(r.triggered_by)}</td>
            <td class="muted">${escHtml(r.error_message ?? "")}</td>
          </tr>`).join("")}
        </tbody>
      </table>`
    : `<p class="muted">No orchestrator runs yet. Run <code>orchestrate-weekly</code> manually or wait for Sunday.</p>`;

  const movementsTable = movements.length
    ? `<table class="ops-table">
        <thead><tr>
          <th>Topic</th><th>Metric</th><th>Prev</th><th>Now</th><th>Δ</th><th>Why</th>
        </tr></thead>
        <tbody>${movements.slice(0, 30).map((m) => `
          <tr>
            <td>${escHtml(m.topic_name)}</td>
            <td>${escHtml(m.metric_name)}</td>
            <td>${m.score_prev ?? "—"}</td>
            <td><strong>${m.score_now}</strong></td>
            <td class="${m.delta > 0 ? "delta-up" : m.delta < 0 ? "delta-down" : ""}">${m.delta > 0 ? "+" : ""}${m.delta}</td>
            <td class="just">${escHtml(m.justification ?? "")}</td>
          </tr>`).join("")}
        </tbody>
      </table>`
    : `<p class="muted">No week-over-week movements yet — comes alive after the second cron run.</p>`;

  host.innerHTML = `
    <div class="ops-wrap">
      <section class="ops-section">
        <h3>Recent orchestrator runs</h3>
        ${runsTable}
      </section>
      <section class="ops-section">
        <h3>Score movements (latest week vs. previous)</h3>
        ${movementsTable}
      </section>
    </div>
  `;

  return () => { host.innerHTML = ""; };
}
